import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ShoppingBasket, Trash2, CheckCheck } from "lucide-react";
import { useGrocery } from "@/lib/grocery";

export function GroceryDrawer({ open, onClose }) {
  const { list, toggle, remove, clear, clearChecked } = useGrocery();
  const checkedCount = list.filter((i) => i.checked).length;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-espresso/40 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.3 }}
            className="fixed right-0 top-0 z-50 flex h-full w-[340px] max-w-[90vw] flex-col bg-cream shadow-[0_20px_40px_rgba(44,30,22,0.2)]"
            data-testid="grocery-drawer"
          >
            <div className="flex items-center justify-between border-b border-espresso/10 px-5 py-4">
              <div className="flex items-center gap-2">
                <ShoppingBasket className="h-5 w-5 text-loretta-red" />
                <h2 className="font-serif text-xl font-bold text-espresso">Grocery List</h2>
              </div>
              <button onClick={onClose} data-testid="grocery-close" className="text-espressolt hover:text-loretta-red">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4">
              {list.length === 0 ? (
                <div className="mt-16 text-center">
                  <ShoppingBasket className="mx-auto mb-3 h-10 w-10 text-espresso/20" />
                  <p className="text-sm text-espresso/50">Your list is empty, sugar.<br />Tap "Add to list" on any recipe.</p>
                </div>
              ) : (
                <ul className="space-y-1.5">
                  {list.map((item) => (
                    <li
                      key={item.id}
                      className="flex items-center gap-3 rounded-2xl bg-white px-3 py-2.5 shadow-[0_2px_8px_rgba(44,30,22,0.04)]"
                    >
                      <button
                        onClick={() => toggle(item.id)}
                        data-testid={`grocery-toggle-${item.id}`}
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                          item.checked ? "border-loretta-olive bg-loretta-olive text-white" : "border-espresso/25"
                        }`}
                      >
                        {item.checked && <CheckCheck className="h-3.5 w-3.5" />}
                      </button>
                      <span className={`flex-1 text-sm ${item.checked ? "text-espresso/40 line-through" : "text-espresso"}`}>
                        {item.text}
                      </span>
                      <button onClick={() => remove(item.id)} className="text-espresso/30 hover:text-loretta-red">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {list.length > 0 && (
              <div className="flex gap-2 border-t border-espresso/10 p-4">
                <button
                  onClick={clearChecked}
                  disabled={checkedCount === 0}
                  data-testid="grocery-clear-checked"
                  className="flex-1 rounded-full border border-espresso/15 py-2.5 text-sm font-medium text-espressolt transition-colors hover:bg-creamalt disabled:opacity-40"
                >
                  Clear checked ({checkedCount})
                </button>
                <button
                  onClick={clear}
                  data-testid="grocery-clear-all"
                  className="rounded-full bg-loretta-red px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-loretta-redhover"
                >
                  Clear all
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
